import { useState } from 'react';
import type { ButtonSlot, IntercomConfig } from '@livepuls/types';
import { Plus } from 'lucide-react';
import { ButtonChip } from './ButtonChip.js';

const DT = 'application/x-livepuls-button-id';

interface Props {
  slot: ButtonSlot;
  draft: IntercomConfig;
  onAdd?: ((position: number) => void) | undefined;
  onButtonClick?: ((buttonId: string) => void) | undefined;
  selectedButtonId?: string | null | undefined;
  /** Button dieses Slots wird gerade angesprochen → orange. */
  receiving?: boolean | undefined;
  /** Macht den enthaltenen ButtonChip per HTML5 ziehbar. */
  draggable?: boolean | undefined;
  /** Drop eines Buttons auf DIESEN Slot → dorthin verschieben (Reposition). */
  onMoveToSlot?: ((buttonId: string, position: number) => void | Promise<void>) | undefined;
  onButtonDragOut?: ((buttonId: string) => void | Promise<void>) | undefined;
  /** Rechtsklick auf belegten Slot → Kontextmenü an Bildschirm-Koordinaten. */
  onButtonContextMenu?:
    | ((buttonId: string, pos: { x: number; y: number }) => void)
    | undefined;
  /** Rechtsklick auf leeren Slot → Kontextmenü (Neuer Button / Einfügen). */
  onEmptyContextMenu?:
    | ((position: number, pos: { x: number; y: number }) => void)
    | undefined;
  /** Remote-Modus: aktueller Pegel + Setter für den Button dieses Slots. */
  volume?: number | undefined;
  onVolumeChange?: ((buttonId: string, volume: number) => void) | undefined;
}

export function PanelSlot({
  slot,
  draft,
  onAdd,
  onButtonClick,
  selectedButtonId,
  receiving,
  draggable,
  onMoveToSlot,
  onButtonDragOut,
  onButtonContextMenu,
  onEmptyContextMenu,
  volume,
  onVolumeChange,
}: Props): JSX.Element {
  const btn = slot.buttonId ? draft.buttons.find((b) => b.id === slot.buttonId) : undefined;
  const [over, setOver] = useState(false);

  // Drop-Handler: einen Button auf DIESEN Slot ziehen → dorthin verschieben.
  const dropProps = onMoveToSlot
    ? {
        onDragOver: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes(DT)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
          if (!over) setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes(DT)) return;
          e.preventDefault();
          e.stopPropagation();
          setOver(false);
          const id = e.dataTransfer.getData(DT);
          if (id) void onMoveToSlot(id, slot.position);
        },
      }
    : {};

  const ring = over ? 'ring-2 ring-accent rounded-btn' : '';

  if (!btn) {
    return (
      <button
        type="button"
        onClick={() => onAdd?.(slot.position)}
        onContextMenu={(e) => {
          if (!onEmptyContextMenu) return;
          e.preventDefault();
          e.stopPropagation();
          onEmptyContextMenu(slot.position, { x: e.clientX, y: e.clientY });
        }}
        {...dropProps}
        className={`slot-empty aspect-[5/3] w-full ${ring}`}
        aria-label={`Slot ${slot.position + 1} hinzufügen`}
      >
        <Plus size={18} />
      </button>
    );
  }

  return (
    <div className={`aspect-[5/3] w-full ${ring}`} {...dropProps}>
      <ButtonChip
        button={btn}
        draft={draft}
        active={selectedButtonId === btn.id}
        receiving={receiving ?? false}
        onClick={() => onButtonClick?.(btn.id)}
        draggable={draggable}
        onDragOut={onButtonDragOut ? () => onButtonDragOut(btn.id) : undefined}
        onContextMenu={
          onButtonContextMenu ? (pos) => onButtonContextMenu(btn.id, pos) : undefined
        }
        volume={volume}
        onVolumeChange={onVolumeChange ? (v) => onVolumeChange(btn.id, v) : undefined}
      />
    </div>
  );
}
